import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// Utility function to handle logout and cleanup
export const logout = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('isAuthenticated');
  localStorage.removeItem('user');
  localStorage.removeItem('tenantId');
  localStorage.removeItem('pendingOtp');
  localStorage.removeItem('pendingAccountId');
  sessionStorage.removeItem('hasGreeted'); // Clear AI greeting flag
  sessionStorage.removeItem('hasSeenSplash'); // Clear Welcome Splash flag
  window.location.href = '/';
};

// Utility to ensure tenantId is a valid string (not a number) and matches accountId format
export function ensureValidTenantId() {
  let tenantId = localStorage.getItem('tenantId');
  // Only clear tenantId if it's empty or all digits (never a valid account name)
  // tenantId is usually a number (accountId), so this regex check was incorrect.
  // Only clear if strictly invalid (which is hard to define without context).
  // For now, assume any non-empty identifier is valid.
  if (!tenantId || tenantId === 'null' || tenantId === 'undefined') {
    localStorage.removeItem('tenantId');
  }
}

// Custom hook for authentication state management
export const useAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('token');
      const authStatus = localStorage.getItem('isAuthenticated') === 'true';
      const userData = localStorage.getItem('user');

      if (token && authStatus && userData) {
        setIsAuthenticated(true);
        setUser(JSON.parse(userData));
      } else {
        setIsAuthenticated(false);
        setUser(null);
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('isAuthenticated', 'true');
    localStorage.setItem('user', JSON.stringify(userData));
    setIsAuthenticated(true);
    setUser(userData);
  };

  const handleLogout = () => {
    logout();
    setIsAuthenticated(false);
    setUser(null);
  };

  const handleUnauthorized = () => {
    handleLogout();
  };

  return {
    isAuthenticated,
    user,
    loading,
    login,
    logout: handleLogout,
    handleUnauthorized
  };
};

// Higher-order component for protecting routes
export const withAuth = (Component) => {
  return (props) => {
    const { isAuthenticated, loading } = useAuth();

    if (loading) {
      return <div>Loading...</div>;
    }

    if (!isAuthenticated) {
      return null; // Will be handled by ProtectedRoute component
    }

    return <Component {...props} />;
  };
}; 